import React, { useState, useCallback } from "react";
import { StyleSheet, View } from "react-native";
import { useFocusEffect } from "expo-router";

import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import ThemedScreen from "@/components/ThemedScreen";
import { CustomAlert } from "@/components/ThemedAlert";
import { ThemedList, type ISortOption, type IGroupOption } from "@/components/ThemedList";
import { useThemeColor } from "@/hooks/useThemeColor";
import { ThemedTag } from "@/components/tags/ThemedTag";
import {
  ThemedChip,
  ThemedCompactCard,
  ThemedMediumCard,
  ThemedFullCard,
  ThemedCustomCard,
} from "@/components/cards";

import { useFirebase } from "@components/firebase/useFirebase";
import { getAllTags } from "@/services/firebase/tag-functions";
import { TagModel, formatDisplayDate } from "common247";

const TAG_SORT_OPTIONS: ISortOption<TagModel>[] = [
  { key: "value", label: "Tag Name (A-Z)", getValue: (t) => t.value || "" },
  { key: "tagType", label: "Tag Type", getValue: (t) => t.tagType || "" },
  { key: "owner", label: "User", getValue: (t) => t.ownerDisplayName || "" },
  { key: "createdAt", label: "Created Date", getValue: (t) => t.createdAt || 0 },
];

const TAG_GROUP_OPTIONS: IGroupOption<TagModel>[] = [
  {
    key: "owner",
    label: "User",
    getGroupValue: (t) => t.ownerDisplayName || "Unknown User",
  },
  {
    key: "tagType",
    label: "Tag Type",
    getGroupValue: (t) => (t.tagType ? t.tagType.toUpperCase() : "GENERAL"),
  },
  {
    key: "year",
    label: "Year",
    getGroupValue: (t) => (t.createdAt ? String(new Date(t.createdAt).getFullYear()) : "No Date"),
  },
];

/**
 * Administrative screen displaying all platform tags grouped by user with view, sort, group, and filter capabilities.
 * Strictly read-only for platform auditing and management.
 */
export default function TagAdminScreen() {
  const services = useFirebase();
  const textMuted = useThemeColor({}, "textMuted");
  const cardBg = useThemeColor({}, "cardBackground");
  const borderColor = useThemeColor({}, "cardBorder");
  const tintColor = useThemeColor({}, "tint");

  const [tags, setTags] = useState<TagModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async (isRefresh = false) => {
    if (!services) return;
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      const response = await getAllTags(services, {});
      if (response.status) {
        setTags(response.tags || []);
      } else {
        CustomAlert.alert("Error", response.error || "Failed to fetch tags.");
      }
    } catch (error) {
      console.error("Error fetching tags:", error);
      CustomAlert.alert("Error", "An unexpected error occurred while fetching tags.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [services]);

  useFocusEffect(
    useCallback(() => {
      fetchData();
    }, [fetchData])
  );

  const distinctUsersCount = new Set(tags.map((t) => t.ownerId).filter(Boolean)).size;

  return (
    <ThemedScreen title="Tag Admin" loading={loading} scrollable={false}>
      <ThemedView style={styles.container}>
        <ThemedText type="stamp" style={{ color: textMuted, marginBottom: 6 }}>
          {`${tags.length} TOTAL ${tags.length === 1 ? "TAG" : "TAGS"} ACROSS ${distinctUsersCount} ${distinctUsersCount === 1 ? "USER" : "USERS"}`}
        </ThemedText>

        <ThemedList<TagModel>
          data={tags}
          sortOptions={TAG_SORT_OPTIONS}
          defaultSortKey="value"
          defaultSortDirection="asc"
          groupOptions={TAG_GROUP_OPTIONS}
          defaultGroupKey="owner"
          persistenceKey="admin-tags"
          pageSize={30}
          filterKeys={["value", "tagType", "ownerDisplayName"]}
          filterPlaceholder="Search all tags..."
          loading={loading}
          onRefresh={() => fetchData(true)}
          refreshing={refreshing}
          emptyText="No tags found in the system."
          contentContainerStyle={styles.listContent}
          renderItem={(item, _index, _isHovered, viewMode) => {
            const formattedDate = item.createdAt ? formatDisplayDate(item.createdAt) : undefined;

            const linkedMedia = (item.mediaList && item.mediaList.length > 0)
              ? item.mediaList
              : item.tagType === "media" && item.targetId
                ? [{ id: item.targetId, title: item.targetTitle || "Media", downloadUrl: item.targetThumbnailUrl, url: item.targetThumbnailUrl }]
                : [];
            const linkedMemories = (item.memoriesList && item.memoriesList.length > 0)
              ? item.memoriesList
              : item.tagType === "memory" && item.targetId
                ? [{ id: item.targetId, title: item.targetTitle || "Memory" }]
                : [];
            const linkedContacts = (item.contactsList && item.contactsList.length > 0)
              ? item.contactsList
              : (item.tagType === "contact" || item.tagType === "user") && item.targetId
                ? [{ id: item.targetId, displayName: item.targetTitle || "Contact", photoUrl: item.targetThumbnailUrl }]
                : [];
            const linkedGroups = (item.groupsList && item.groupsList.length > 0)
              ? item.groupsList
              : item.tagType === "group" && item.targetId
                ? [{ id: item.targetId, name: item.targetTitle || "Group" }]
                : [];

            if (viewMode === "compact") {
              return (
                <ThemedCompactCard
                  entityType="tag"
                  id={item.id}
                  title={item.value}
                  tagType={item.tagType}
                  date={formattedDate}
                />
              );
            }

            if (viewMode === "medium") {
              return (
                <ThemedMediumCard
                  entityType="tag"
                  id={item.id}
                  title={item.value}
                  tagType={item.tagType}
                  date={formattedDate}
                  ownerDisplayName={item.ownerDisplayName}
                  mediaList={linkedMedia}
                  memoriesList={linkedMemories}
                  contactsList={linkedContacts}
                  groupsList={linkedGroups}
                  isOwner={false}
                  canShare={false}
                />
              );
            }

            if (viewMode === "full") {
              return (
                <ThemedFullCard
                  entityType="tag"
                  id={item.id}
                  title={item.value}
                  tagType={item.tagType}
                  date={formattedDate}
                  ownerDisplayName={item.ownerDisplayName}
                  mediaList={linkedMedia}
                  memoriesList={linkedMemories}
                  contactsList={linkedContacts}
                  groupsList={linkedGroups}
                  isOwner={false}
                  canShare={false}
                />
              );
            }

            if (viewMode === "custom") {
              return (
                <ThemedCustomCard
                  entityType="tag"
                  id={item.id}
                  title={item.value}
                  formattedDate={formattedDate}
                  mediaList={linkedMedia}
                  memoriesList={linkedMemories}
                  contactsList={linkedContacts}
                  groupsList={linkedGroups}
                  item={{
                    id: item.id,
                    title: item.value,
                    tagType: item.tagType,
                    createdAt: item.createdAt,
                    ownerDisplayName: item.ownerDisplayName,
                    mediaList: linkedMedia,
                    memoriesList: linkedMemories,
                    contactsList: linkedContacts,
                    groupsList: linkedGroups,
                  }}
                  isOwner={false}
                  canShare={false}
                />
              );
            }

            // Default card tier view: Read-only tag card specimen
            return (
              <ThemedView
                style={[
                  styles.tagCard,
                  {
                    backgroundColor: cardBg,
                    borderColor: borderColor,
                  },
                ]}
              >
                <View style={styles.tagCardHeader}>
                  <ThemedTag tag={item} readOnly={true} compact={false} />
                  <ThemedText style={[styles.ownerBadge, { color: textMuted }]}>
                    {item.ownerDisplayName || "Unknown User"}
                  </ThemedText>
                </View>

                <View style={styles.tagCardDetails}>
                  <ThemedText style={[styles.metaText, { color: textMuted }]}>
                    Type: <ThemedText type="defaultSemiBold">{item.tagType || "general"}</ThemedText>
                  </ThemedText>
                  {formattedDate ? (
                    <ThemedText style={[styles.metaText, { color: textMuted }]}>
                      Created: {formattedDate}
                    </ThemedText>
                  ) : null}
                  {item.targetId ? (
                    <ThemedChip
                      entityType={
                        item.tagType === "media"
                          ? "media"
                          : item.tagType === "memory"
                            ? "memory"
                            : item.tagType === "group"
                              ? "group"
                              : "contact"
                      }
                      id={item.targetId}
                      label={item.targetTitle || item.targetId}
                      thumbnailUrl={item.targetThumbnailUrl}
                    />
                  ) : null}
                </View>
              </ThemedView>
            );
          }}
        />
      </ThemedView>
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 8,
    minHeight: 0,
  },
  listContent: {
    paddingBottom: 24,
    gap: 6,
  },
  tagCard: {
    borderWidth: 1,
    borderRadius: 4,
    padding: 10,
    marginBottom: 4,
  },
  tagCardHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 6,
  },
  ownerBadge: {
    fontSize: 12,
    fontWeight: "500",
  },
  tagCardDetails: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
    alignItems: "center",
  },
  metaText: {
    fontSize: 11,
  },
});
